import { AuthService } from "./AuthService";
import { OnlineFeaturesStorage } from "./OnlineFeaturesStorage";
import { getLeaderboardService } from "./LeaderboardService";
import { endlessLeaderboardService } from "@/endless/EndlessLeaderboardService";

let started = false;

/**
 * PR34: signs in (reusing the existing session - `AuthService` dedupes
 * concurrent callers) and then flushes BOTH offline queues, Campaign's and
 * Endless's. The single place every "Online Features just became (or
 * already were) available" path funnels through: app startup, the
 * browser's `online` event, and the ENABLE button on either leaderboard
 * screen. A no-op while Online Features are off - `ensureSignedIn()` makes
 * no network call then, and both services' flushes are themselves gated on
 * the opt-in, so anything captured before opt-in stays on the device.
 * Never throws.
 */
export async function flushOnlineQueues(): Promise<void> {
  if (!(await AuthService.ensureSignedIn())) return;
  await Promise.all([getLeaderboardService().flushQueue(), endlessLeaderboardService.flushQueue()]);
}

/**
 * Called once at app startup. If the player previously enabled Online
 * Features, silently re-establishes their session and flushes anything
 * left in the offline run queue - Campaign's and Endless's alike, each its
 * own queue - all fire-and-forget, never blocking the game from loading.
 * If they haven't (or chose PLAY OFFLINE), this does nothing at all, and
 * stays doing nothing: no Supabase call is ever made before the player
 * opts in from the ONLINE FEATURES prompt.
 *
 * Also wires the browser's `online` event to retry a previously failed
 * sign-in and flush both queues once connectivity returns.
 */
export function bootstrapOnlineFeatures(): void {
  if (started) return;
  started = true;

  if (OnlineFeaturesStorage.isEnabled()) {
    void flushOnlineQueues();
  }

  globalThis.window?.addEventListener?.("online", () => {
    if (!OnlineFeaturesStorage.isEnabled()) return;
    AuthService.retryIfNeeded();
    void flushOnlineQueues();
  });
}
